import { useCallback, useEffect, useRef, useState } from "react";

import { UnifiedCarouselImage } from "@/src/types/carousel";

export interface ImageLoadStatus {
  xl: boolean;
  original: boolean;
}

export type ImageLoadStatusMap = Record<string, ImageLoadStatus>;

interface UseProgressiveImageLoaderReturn {
  imageLoadStatus: ImageLoadStatusMap;
  priorityLoadOriginal: (image: UnifiedCarouselImage) => Promise<void>;
}

// Batch size for loading images to avoid overwhelming browser connections
const BATCH_SIZE = 4; // Load 4 images at a time
// Delay between batches in milliseconds
const BATCH_DELAY_MS = 200;

const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // Resolve even on error to continue loading
    img.src = src;
  });
};

// Polyfill for requestIdleCallback for browsers that don't support it
const requestIdleCallbackPolyfill = (
  callback: (deadline: {
    timeRemaining: () => number;
    didTimeout: boolean;
  }) => void,
  options?: { timeout?: number }
): number => {
  const timeout = options?.timeout ?? 0;
  const start = Date.now();

  return setTimeout(() => {
    callback({
      timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      didTimeout: Date.now() - start >= timeout
    });
  }, 1) as unknown as number;
};

const cancelIdleCallbackPolyfill = (id: number): void => {
  clearTimeout(id);
};

// Use native requestIdleCallback if available, otherwise use polyfill
const requestIdleCallbackSafe =
  typeof requestIdleCallback === "function"
    ? requestIdleCallback
    : requestIdleCallbackPolyfill;

const cancelIdleCallbackSafe =
  typeof cancelIdleCallback === "function"
    ? cancelIdleCallback
    : cancelIdleCallbackPolyfill;

// Check if document is visible (not in background tab)
const isDocumentVisible = (): boolean => {
  if (typeof document === "undefined") return true;
  return document.visibilityState === "visible";
};

// Load images in batches to avoid connection saturation
const loadImagesInBatches = async (
  images: UnifiedCarouselImage[],
  getUrl: (img: UnifiedCarouselImage) => string | undefined,
  onLoad: (imageId: string) => void
): Promise<void> => {
  const imagesToLoad = images
    .map((img) => ({
      image: img,
      url: getUrl(img)
    }))
    .filter((item) => item.url !== undefined) as Array<{
    image: UnifiedCarouselImage;
    url: string;
  }>;

  if (imagesToLoad.length === 0) {
    return;
  }

  // Load images in batches
  for (let i = 0; i < imagesToLoad.length; i += BATCH_SIZE) {
    // Stop loading if document becomes hidden
    if (!isDocumentVisible()) {
      break;
    }

    const batch = imagesToLoad.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async ({ image, url }) => {
      await preloadImage(url);
      onLoad(image.id);
    });

    await Promise.all(batchPromises);

    // Small delay between batches to avoid overwhelming the browser
    if (i + BATCH_SIZE < imagesToLoad.length) {
      await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
};

const useProgressiveImageLoader = (
  images: UnifiedCarouselImage[]
): UseProgressiveImageLoaderReturn => {
  const [imageLoadStatus, setImageLoadStatus] = useState<ImageLoadStatusMap>(
    {}
  );
  const loadingRef = useRef(false);
  const imageIdsRef = useRef<string[]>([]);
  const isMountedRef = useRef(true);
  const idleCallbackRef = useRef<number | null>(null);

  const initializeStatus = useCallback((imgs: UnifiedCarouselImage[]) => {
    const initialStatus: ImageLoadStatusMap = {};
    imgs.forEach((img) => {
      initialStatus[img.id] = { xl: false, original: false };
    });
    return initialStatus;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any pending idle callbacks on unmount
      if (idleCallbackRef.current !== null) {
        cancelIdleCallbackSafe(idleCallbackRef.current);
        idleCallbackRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const currentImageIds = images.map((img) => img.id).join(",");
    const prevImageIds = imageIdsRef.current.join(",");

    if (currentImageIds === prevImageIds) {
      return;
    }

    imageIdsRef.current = images.map((img) => img.id);
    loadingRef.current = false;
    setImageLoadStatus(initializeStatus(images));
  }, [images, initializeStatus]);

  const updateXlStatus = useCallback((imageId: string) => {
    setImageLoadStatus((prev) => {
      if (!isMountedRef.current) return prev;
      return {
        ...prev,
        [imageId]: { ...prev[imageId], xl: true }
      };
    });
  }, []);

  const updateOriginalStatus = useCallback((imageId: string) => {
    setImageLoadStatus((prev) => {
      if (!isMountedRef.current) return prev;
      return {
        ...prev,
        [imageId]: { ...prev[imageId], original: true }
      };
    });
  }, []);

  const loadXlImages = useCallback(
    async (imgs: UnifiedCarouselImage[]) => {
      await loadImagesInBatches(
        imgs,
        (img) =>
          img.lightboxUrl && img.lightboxUrl !== img.url
            ? img.lightboxUrl
            : undefined,
        updateXlStatus
      );
    },
    [updateXlStatus]
  );

  const loadOriginalImages = useCallback(
    async (imgs: UnifiedCarouselImage[]) => {
      await loadImagesInBatches(
        imgs,
        (img) =>
          img.originalUrl && img.originalUrl !== img.lightboxUrl
            ? img.originalUrl
            : undefined,
        updateOriginalStatus
      );
    },
    [updateOriginalStatus]
  );

  // Shared function to schedule background image loading
  const scheduleBackgroundLoad = useCallback(() => {
    // Cancel any existing idle callback
    if (idleCallbackRef.current !== null) {
      cancelIdleCallbackSafe(idleCallbackRef.current);
      idleCallbackRef.current = null;
    }

    // Don't schedule if already loading or no images
    if (loadingRef.current || images.length === 0) {
      return;
    }

    // Don't schedule if document is hidden
    if (!isDocumentVisible()) {
      return;
    }

    loadingRef.current = true;

    const loadImages = async () => {
      // Only load if component is still mounted and document is visible
      if (!isMountedRef.current || !isDocumentVisible()) {
        loadingRef.current = false;
        return;
      }

      // Load XL images first (prioritized for lightbox)
      await loadXlImages(images);

      // Only load originals if component is still mounted and document is visible
      if (!isMountedRef.current || !isDocumentVisible()) {
        loadingRef.current = false;
        return;
      }

      // Small delay before loading originals to prioritize XL images
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Load original images after XL images are done
      if (isMountedRef.current && isDocumentVisible()) {
        await loadOriginalImages(images);
      }

      // Mark loading as complete
      loadingRef.current = false;
    };

    // Use requestIdleCallback for non-blocking background loading
    idleCallbackRef.current = requestIdleCallbackSafe(
      (deadline) => {
        // Start loading immediately if we have time, otherwise defer
        if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
          loadImages();
        } else {
          // Not enough time in this idle period, schedule for next
          idleCallbackRef.current = requestIdleCallbackSafe(
            () => loadImages(),
            { timeout: 2000 }
          );
        }
      },
      { timeout: 2000 } // Ensure loading starts within 2 seconds even if browser is busy
    );
  }, [images, loadXlImages, loadOriginalImages]);

  // Initial effect: schedule loading when images change and document is visible
  useEffect(() => {
    scheduleBackgroundLoad();

    return () => {
      // Cancel idle callback and reset loading state on cleanup
      if (idleCallbackRef.current !== null) {
        cancelIdleCallbackSafe(idleCallbackRef.current);
        idleCallbackRef.current = null;
      }
      loadingRef.current = false;
    };
  }, [scheduleBackgroundLoad]);

  // Resume loading when document becomes visible again
  useEffect(() => {
    if (images.length === 0) {
      return;
    }

    const handleVisibilityChange = () => {
      if (isDocumentVisible()) {
        // Schedule loading when tab becomes visible
        scheduleBackgroundLoad();
      } else {
        // Cancel idle callback when tab becomes hidden
        if (idleCallbackRef.current !== null) {
          cancelIdleCallbackSafe(idleCallbackRef.current);
          idleCallbackRef.current = null;
        }
        loadingRef.current = false;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [images, scheduleBackgroundLoad]);

  // Priority load for a specific image's original URL (used when lightbox opens)
  const priorityLoadOriginal = useCallback(
    async (image: UnifiedCarouselImage) => {
      if (!isMountedRef.current) return;

      const hasOriginalUrl =
        image.originalUrl && image.originalUrl !== image.lightboxUrl;
      const hasLightboxUrl =
        image.lightboxUrl && image.lightboxUrl !== image.url;

      // First ensure XL is loaded
      if (hasLightboxUrl && image.lightboxUrl) {
        await preloadImage(image.lightboxUrl);
        updateXlStatus(image.id);
      }

      // Then load original if available
      if (hasOriginalUrl && image.originalUrl) {
        await preloadImage(image.originalUrl);
        updateOriginalStatus(image.id);
      }
    },
    [updateXlStatus, updateOriginalStatus]
  );

  return {
    imageLoadStatus,
    priorityLoadOriginal
  };
};

export default useProgressiveImageLoader;
